import { useMemo, useState } from "react";
import { scarpaGray400, spanishViridian500 } from "@habitech/shared";
import Icon from "src/components/common/Icon";
import { useAppDispatch, useAppSelector } from "src/hooks/redux";
import {
    selectCommercialCartSelection,
    selectDataSuggested,
    selectHasInputErrors,
    selectHasLastname,
    selectHasName,
    selectProfileCompletenessNotice,
    selectSelectedMonths,
    selectSelectedPaymentMethod,
} from "src/redux/shopping_cart/shopping_cart.selectors";
import { toggleProductSelection } from "src/redux/shopping_cart/shopping_cart.slice";
import { ProductByCategory } from "src/redux/shopping_cart/shopping_cart.initial";
import { getProductDisplayName, ORDERED_CATEGORIES_FOR_DISPLAY, SUBCATEGORY_PAYLOAD_OVERRIDE, Subcategory } from "../ProducsSelectable/CommercialCart.constants";
import {
    getPaymentMethodLimitExceededMessage,
    isPaymentMethodDisallowedForTotal,
    methodsName,
    payment_method,
    pyament_method_str,
} from "src/constants/ShoppingCart";
import ButtonPaymentOneClick from "src/components/ButtonPaymentOneClick/ButtonPaymentOneClick";
import ButtonMercadoPago from "./ButtonMercadoPago";
import {
    Card,
    CheckboxRow,
    Divider,
    EmptyState,
    Header,
    PaymentRow,
    PeriodChip,
    ProductList,
    ProductRow,
    TotalRow,
} from "./OrderSummary.styles";
import { selectUser } from "src/redux/user/userSlice";
import { useSelector } from "react-redux";

function getPaymentMethodLabel(value: number): string {
    const entry = Object.entries(payment_method).find(([, v]) => v === value);
    if (!entry) return "Sin seleccionar";
    const key = entry[0] as keyof typeof pyament_method_str;
    return pyament_method_str[key] ?? "Sin seleccionar";
}

const OrderSummary = () => {
    const dispatch = useAppDispatch();
    const user = useSelector(selectUser);
    const { suggested } = useAppSelector(selectDataSuggested);
    const { selectedByProductId, quantityByProductId, priceByProductId, msiEnabled, msiMonths, eliteZoneByProductId } = useAppSelector(selectCommercialCartSelection);
    const selectedMonths = useAppSelector(selectSelectedMonths);
    const selectedPaymentMethod = useAppSelector(selectSelectedPaymentMethod);
    const hasInputErrors = useAppSelector(selectHasInputErrors);
    const hasLastname = useAppSelector(selectHasLastname);
    const hasName = useAppSelector(selectHasName);
    const profileNotice = useAppSelector(selectProfileCompletenessNotice);
    const [leadsChecked, setLeadsChecked] = useState(false);
    const [disclaimerChecked, setDisclaimerChecked] = useState(false);

    const getAllProducts = (): ProductByCategory[] =>
        Object.values(suggested.products.category).flat();

    const selectedProductsRaw = getAllProducts().filter((p) => selectedByProductId[p.product_id]);
    const selectedProducts = [...selectedProductsRaw].sort((a, b) => {
        const indexA = ORDERED_CATEGORIES_FOR_DISPLAY.indexOf(a.category);
        const indexB = ORDERED_CATEGORIES_FOR_DISPLAY.indexOf(b.category);
        if (indexA !== indexB) return indexA - indexB;
        return a.product_id - b.product_id;
    });

    // Para mostrar en el resumen: oculta productos Elite que aún no tienen zona seleccionada
    const selectedProductsForDisplay = selectedProducts.filter(
        (p) => p.category !== 'elite' || !!eliteZoneByProductId[p.product_id]
    );

    const total = selectedProductsForDisplay.reduce(
        (sum, p) => sum + (priceByProductId[p.product_id] ?? 0),
        0
    );
    const periodLabel = selectedMonths === null ? "Sin periodo" : `${selectedMonths} meses`;
    const paymentLabel =
        selectedPaymentMethod === null ? "Sin seleccionar" : getPaymentMethodLabel(selectedPaymentMethod);

    // Revisa TODOS los Elite seleccionados (incluyendo sin zona) para bloquear el pago
    const allEliteHaveZone = selectedProducts
        .filter((p) => p.category === 'elite')
        .every((p) => !!eliteZoneByProductId[p.product_id]);

    const paymentBlockedByAmount = isPaymentMethodDisallowedForTotal(selectedPaymentMethod, total);

    const ctaFooterNoticeMessage = (() => {
        if (paymentBlockedByAmount) return getPaymentMethodLimitExceededMessage(selectedPaymentMethod);
        return profileNotice;
    })();

    const isMercadoPago = selectedPaymentMethod === payment_method[methodsName.MERCADOPAGO];

    const canSubmit =
        selectedProducts.length > 0 &&
        selectedMonths !== null &&
        selectedPaymentMethod !== null &&
        !paymentBlockedByAmount &&
        leadsChecked &&
        disclaimerChecked &&
        !hasInputErrors &&
        (!msiEnabled || msiMonths !== null) &&
        allEliteHaveZone &&
        hasLastname &&
        hasName;
    
    const paymentDetail = useMemo(() => {
        if (selectedMonths === null) return "[]";
        return JSON.stringify(
            selectedProductsForDisplay.map((product) => {
                const displayName = getProductDisplayName(product.name_product, product.subcategory);
                const quantity = quantityByProductId[product.product_id] ?? 0;
                const payloadOverride = SUBCATEGORY_PAYLOAD_OVERRIDE[product.subcategory?.toLowerCase() as Subcategory];
                const item: Record<string, unknown> = {
                    cart_product_id: payloadOverride?.cart_product_id ?? product.product_id,
                    category: payloadOverride?.category ?? product.category,
                    months: selectedMonths,
                    quantity,
                    price: priceByProductId[product.product_id] ?? 0,
                    description: `${quantity} ${displayName} por ${selectedMonths} ${selectedMonths === 1 ? "mes" : "meses"}`,
                    user_id: user?.id,
                };
                if (product.category === "Destacado") item.classification_id = 1;
                if (product.category === "Leads") item.quantity_paid_leads = "99999";
                if (product.category === "smart_leads") item.months  = 12;
                if (product.category === "elite") item.elite_zone = eliteZoneByProductId[product.product_id] ?? null;
                return item;
            })
        );
    }, [selectedProductsForDisplay, selectedMonths, quantityByProductId, priceByProductId, eliteZoneByProductId, user?.id]);

    const handleRemove = (product: ProductByCategory) => {
        dispatch(
            toggleProductSelection({
                productId: product.product_id,
                willBeSelected: false,
                productIdsToClear: [product.product_id],
            })
        );
    };

    return (
        <Card>
            <Header>
                <h2 className="header-title">
                    <Icon icon="pcom-icon-cart" size={22} style={{ color: spanishViridian500 }} />
                    Tu pedido
                </h2>
                <PeriodChip>{periodLabel}</PeriodChip>
            </Header>

            {selectedProductsForDisplay.length === 0 ? (
                <EmptyState>
                    <Icon icon="pcom-icon-cart" size={48} style={{ color: scarpaGray400 }} />
                    <span>Agrega productos</span>
                </EmptyState>
            ) : (
                <ProductList>
                    {selectedProductsForDisplay.map((product) => {
                        const displayName = getProductDisplayName(product.name_product, product.subcategory);
                        return (
                            <ProductRow key={product.product_id}>
                                <div className="product-info">
                                    <span className="product-name">{displayName}</span>
                                    <span className="product-quantity">
                                        {quantityByProductId[product.product_id] ?? 0} cantidad
                                    </span>
                                </div>
                                <span className="product-price">
                                    ${priceByProductId[product.product_id] ?? 0}
                                </span>
                                <button
                                    type="button"
                                    className="remove-btn"
                                    onClick={() => handleRemove(product)}
                                    aria-label={`Quitar ${displayName}`}
                                >
                                    <Icon icon="pcom-icon-close" size={10} />
                                </button>
                            </ProductRow>
                        );
                    })}
                </ProductList>
            )}

            <Divider />
            <TotalRow>
                <span className="total-label">Total</span>
                <span className="total-amount">${total}</span>
            </TotalRow>
            <Divider />
            <PaymentRow>
                <span className="payment-label">
                    <Icon icon="pcom-icon-credit-card-2" size={18} style={{ color: spanishViridian500 }} />
                    Pago
                </span>
                <span className="payment-value">{paymentLabel}</span>
            </PaymentRow>
            <CheckboxRow>
                <input
                    type="checkbox"
                    checked={leadsChecked}
                    onChange={(e) => setLeadsChecked(e.target.checked)}
                />
                <span>
                    Entiendo que Leads de WhatsApp se incluye automáticamente en todos los productos del catálogo
                </span>
            </CheckboxRow>
            <CheckboxRow>
                <input
                    type="checkbox"
                    checked={disclaimerChecked}
                    onChange={(e) => setDisclaimerChecked(e.target.checked)}
                />
                <span>
                    Confirmo que esta venta cumple con lineamientos comerciales y regulatorios
                </span>
            </CheckboxRow>
            {isMercadoPago ? (
                <ButtonMercadoPago
                    selectedProducts={selectedProductsForDisplay}
                    selection={{
                        quantityByProductId,
                        priceByProductId,
                        msiEnabled,
                        msiMonths,
                    }}
                    selectedMonths={selectedMonths}
                    userId={suggested.users.user_id}
                    payerEmail={suggested.users.email}
                    userProfile={{
                        name: suggested.user_profile?.name ?? '',
                        lastname: suggested.user_profile?.lastname ?? '',
                    }}
                    buttonDisabled={!canSubmit}
                    ctaFooterNotice={ctaFooterNoticeMessage}
                />
            ) : (
                <ButtonPaymentOneClick
                    total={total}
                    description="Carrito comercial"
                    detail={paymentDetail}
                    buttonDisabled={!canSubmit}
                    user_id={suggested.users.user_id}
                    type_payment={selectedPaymentMethod ?? undefined}
                    msi_months={msiMonths ?? undefined}
                    ctaFooterNotice={ctaFooterNoticeMessage}
                />
            )}
        </Card>
    );
};

export default OrderSummary;
